/**
 * Store Info page: which store the picker shows, and how a store's status
 * reads as a pill.
 *
 * Plain functions in their own .ts file (not StoreInfo.tsx) so react-refresh's
 * only-export-components rule doesn't flag mixing them with a component, and
 * so the precedence rules are unit-testable without rendering. Same split as
 * inventoryStoreParam.ts.
 *
 * Precedence for the picked store, per the spec: `?store=NNNN` in the URL
 * wins, then the last pick remembered in localStorage, then the first store in
 * the list. A remembered or URL store that is no longer in the list is ignored
 * rather than shown as an empty picker.
 */

import type { StoreInfoPickerStore } from '@/lib/useStoreInfo';

export const LAST_STORE_INFO_STORE_KEY = 'cockpit.storeInfo.lastStore';

export function readLastStoreInfoStore(): string | null {
  try {
    const raw = window.localStorage.getItem(LAST_STORE_INFO_STORE_KEY);
    return raw && raw.trim() ? raw.trim() : null;
  } catch {
    return null;
  }
}

export function writeLastStoreInfoStore(storeNo: string): void {
  try {
    window.localStorage.setItem(LAST_STORE_INFO_STORE_KEY, storeNo);
  } catch {
    // Ignore storage errors (private mode, quota, etc.).
  }
}

/**
 * The store to show: the URL param, else the remembered pick, else the first
 * store. Returns null while the list is still empty (loading, or no stores),
 * so the caller can hold off fetching details.
 */
export function resolveStoreInfoStore(
  urlStore: string | null,
  remembered: string | null,
  stores: StoreInfoPickerStore[],
): string | null {
  const first = stores[0];
  if (!first) return null;
  const has = (v: string | null): v is string => !!v && stores.some((s) => s.store_no === v);
  if (has(urlStore)) return urlStore;
  if (has(remembered)) return remembered;
  return first.store_no;
}

export type StoreStatusKind = 'open' | 'closed' | 'other';

/**
 * `editable.store_status` is free-ish text from the store record, so anything
 * that isn't recognisably open or closed falls back to 'other' and renders
 * with its own label rather than being hidden.
 */
export function storeStatusKind(status: string | null | undefined): StoreStatusKind {
  const s = (status ?? '').trim().toLowerCase();
  if (!s) return 'other';
  if (s === 'open' || s === 'active') return 'open';
  if (s === 'closed' || s === 'inactive') return 'closed';
  return 'other';
}

export function storeStatusLabel(status: string | null | undefined): string {
  const kind = storeStatusKind(status);
  if (kind === 'open') return 'Open';
  if (kind === 'closed') return 'Closed';
  const raw = (status ?? '').trim();
  return raw || 'Status unknown';
}
